import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { FaPhone, FaMicrophone, FaMicrophoneSlash, FaVolumeUp, FaVolumeMute, FaVideo, FaVideoSlash, FaPhoneSlash, FaPause, FaPlay } from 'react-icons/fa';
import CallService from '../../services/callService';
import CallHistoryService from '../../services/callHistoryService';

interface CallInterfaceProps {
  recipientId: string;
  recipientName: string;
  onCallEnd: () => void;
}

const CallContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  flex-direction: column;
  z-index: 1000;
`;

const VideoContainer = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #000;
`;

const RemoteVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const LocalVideo = styled.video`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 200px;
  height: 150px;
  border-radius: 8px;
  border: 2px solid #fff;
  object-fit: cover;
`;

const ControlsContainer = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 20px 0;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  justify-content: center;
  gap: 20px;
  z-index: 2000;
`;

const ControlButton = styled.button<{ active?: boolean }>`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  border: none;
  background: ${props => props.active ? '#4CAF50' : '#333'};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.active ? '#45a049' : '#444'};
  }

  &.end-call {
    background: #f44336;
    &:hover {
      background: #d32f2f;
    }
  }
`;

const CallInfoBox = styled.div`
  position: absolute;
  top: 32px;
  left: 32px;
  display: flex;
  align-items: center;
  gap: 1.25rem;
  background: rgba(30, 27, 75, 0.85);
  padding: 18px 32px;
  border-radius: 32px;
  box-shadow: 0 4px 24px rgba(30, 27, 75, 0.12);
  animation: fadeIn 0.5s ease;
  z-index: 2;

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-16px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const AvatarCircle = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #7e22ce;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(126, 34, 206, 0.15);
`;

const CallInfoText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const CallName = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: #fff;
`;

const CallDuration = styled.div`
  font-size: 1.1rem;
  font-weight: 500;
  color: #fbbf24;
  letter-spacing: 1px;
`;

const QualityIndicator = styled.div<{ quality: string }>`
  font-size: 0.95rem;
  font-weight: 500;
  color: ${({ quality }) =>
    quality === 'good' ? '#22c55e' : quality === 'fair' ? '#fbbf24' : '#ef4444'};
`;

const CallInterface: React.FC<CallInterfaceProps> = ({
  recipientId,
  recipientName,
  onCallEnd
}) => {
  const [callState, setCallState] = useState(CallService.getInstance().getCallState());
  const [duration, setDuration] = useState(0);
  const [callQuality, setCallQuality] = useState<'good' | 'fair' | 'poor'>('good');
  const [isOnHold, setIsOnHold] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [recordedChunks, setRecordedChunks] = useState<Blob[]>([]);
  const [isLocalStreamReady, setIsLocalStreamReady] = useState(false);
  const [streamError, setStreamError] = useState<string | null>(null);
  
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const callService = CallService.getInstance();
  const callHistoryService = CallHistoryService.getInstance();
  const durationInterval = useRef<NodeJS.Timeout>();
  const statsInterval = useRef<NodeJS.Timeout>();
  const callId = useRef<string>();
  const localStreamRef = useRef<MediaStream | null>(null);
  const isCleaningUp = useRef<boolean>(false);

  // Initialize local stream
  useEffect(() => {
    const initializeLocalStream = async () => {
      try {
        console.log('[CallInterface] Initializing local stream...');
        
        // First try to get all available devices
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(device => device.kind === 'videoinput');
        const audioDevices = devices.filter(device => device.kind === 'audioinput');
        
        console.log('[CallInterface] Available video devices:', videoDevices);
        console.log('[CallInterface] Available audio devices:', audioDevices);

        // Try to get the stream with specific device IDs if available
        let stream: MediaStream;
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: {
              deviceId: videoDevices[0]?.deviceId ? { exact: videoDevices[0].deviceId } : undefined
            },
            audio: {
              deviceId: audioDevices[0]?.deviceId ? { exact: audioDevices[0].deviceId } : undefined
            }
          });
        } catch (error) {
          console.log('[CallInterface] Failed to get stream with specific devices, falling back to default:', error);
          // Fallback to default devices
          stream = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: true
          });
        }
        
        console.log('[CallInterface] Local stream obtained:', stream.getTracks().map(t => t.kind));
        localStreamRef.current = stream;
        
        if (localVideoRef.current) {
          console.log('[CallInterface] Setting local video stream');
          localVideoRef.current.srcObject = stream;
          try {
            await localVideoRef.current.play();
            console.log('[CallInterface] Local video playback started');
          } catch (error) {
            console.error('[CallInterface] Error playing local video:', error);
          }
        }
        
        setIsLocalStreamReady(true);
        callService.setLocalStream(stream);
        setStreamError(null);
      } catch (error) {
        console.error('[CallInterface] Error accessing media devices:', error);
        setStreamError('Failed to access camera and microphone');
        
        // Fallback to audio-only if video fails
        try {
          console.log('[CallInterface] Attempting audio-only fallback...');
          const audioStream = await navigator.mediaDevices.getUserMedia({
            audio: true,
            video: false
          });
          
          console.log('[CallInterface] Audio-only stream obtained');
          localStreamRef.current = audioStream;
          setIsLocalStreamReady(true);
          callService.setLocalStream(audioStream);
          setStreamError(null);
        } catch (audioError) {
          console.error('[CallInterface] Error accessing audio devices:', audioError);
          setStreamError('Failed to access microphone');
        }
      }
    };

    // Only initialize if we don't have a stream yet
    if (!localStreamRef.current) {
      initializeLocalStream();
    }

    return () => {
      console.log('[CallInterface] Cleaning up local stream...');
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => {
          console.log(`[CallInterface] Stopping track: ${track.kind}`);
          track.stop();
        });
        localStreamRef.current = null;
      }
    };
  }, []);

  // Handle call state changes
  useEffect(() => {
    const handleCallStateChange = (state: any) => {
      console.log('[CallInterface] Call state changed:', state);
      setCallState(state);
      
      // Handle remote stream
      if (state.remoteStream && remoteVideoRef.current) {
        console.log('[CallInterface] Setting remote stream');
        remoteVideoRef.current.srcObject = state.remoteStream;
        remoteVideoRef.current.play().catch(error => {
          console.error('[CallInterface] Error playing remote video:', error);
        });
      }

      // Start duration timer when call is established
      if (state.isInCall && !durationInterval.current) {
        console.log('[CallInterface] Starting call duration timer');
        durationInterval.current = setInterval(() => {
          setDuration(prev => prev + 1);
        }, 1000);
      }

      // Start quality monitoring when call is established
      if (state.isInCall && !statsInterval.current) {
        console.log('[CallInterface] Starting call quality monitoring');
        statsInterval.current = setInterval(async () => {
          try {
            const peerConnection = callService.getPeerConnection();
            if (peerConnection) {
              const stats = await peerConnection.getStats();
              let bitrate = 0;
              let packetLoss = 0;
              let roundTripTime = 0;

              stats.forEach(report => {
                if (report.type === 'inbound-rtp' && report.kind === 'video') {
                  bitrate = report.bytesReceived * 8 / 1000; // kbps
                }
                if (report.type === 'candidate-pair' && report.state === 'succeeded') {
                  roundTripTime = report.currentRoundTripTime * 1000; // ms
                }
              });

              const quality = calculateQuality(bitrate, packetLoss, roundTripTime);
              setCallQuality(quality);
            }
          } catch (error) {
            console.error('[CallInterface] Error getting call stats:', error);
          }
        }, 2000);
      }

      // Stop timers when call ends
      if (!state.isInCall) {
        if (durationInterval.current) {
          clearInterval(durationInterval.current);
          durationInterval.current = undefined;
        }
        if (statsInterval.current) {
          clearInterval(statsInterval.current);
          statsInterval.current = undefined;
        }
      }
    };

    // Subscribe to call state changes
    callService.setCallStateChangeListener(handleCallStateChange);

    return () => {
      callService.setCallStateChangeListener(() => {});
      if (durationInterval.current) {
        clearInterval(durationInterval.current);
      }
      if (statsInterval.current) {
        clearInterval(statsInterval.current);
      }
    };
  }, []);

  // Initialize call when local stream is ready
  useEffect(() => {
    if (isLocalStreamReady && !isCleaningUp.current) {
      console.log('[CallInterface] Local stream ready, initializing call...');
      const initializeCall = async () => {
        try {
          // Ensure local stream is set before starting call
          if (localStreamRef.current) {
            callService.setLocalStream(localStreamRef.current);
            // Add a small delay to ensure stream is properly set
            await new Promise(resolve => setTimeout(resolve, 500));
            await callService.startCall(recipientId, recipientName, '');
            console.log('[CallInterface] Call initialized successfully');
          } else {
            throw new Error('Local stream not available');
          }
        } catch (error) {
          console.error('[CallInterface] Error initializing call:', error);
          setStreamError('Failed to initialize call');
          performCleanup();
        }
      };
      initializeCall();
    }
  }, [isLocalStreamReady, recipientId, recipientName]);

  const calculateQuality = (
    bitrate: number,
    packetLoss: number,
    roundTripTime: number
  ): 'good' | 'fair' | 'poor' => {
    if (bitrate < 10000 || packetLoss > 5 || roundTripTime > 300) {
      return 'poor';
    } else if (bitrate < 20000 || packetLoss > 2 || roundTripTime > 150) {
      return 'fair';
    }
    return 'good';
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleEndCall = () => {
    console.log('[CallInterface] Ending call...');
    performCleanup();
    onCallEnd();
  };

  const handleToggleMute = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        callService.toggleMute();
      }
    }
  };

  const handleToggleVideo = () => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoEnabled(videoTrack.enabled);
        callService.toggleVideo();
      }
    }
  };

  const handleToggleSpeaker = () => {
    callService.toggleSpeaker();
  };

  const handleToggleHold = () => {
    setIsOnHold(!isOnHold);
    callService.toggleHold();
  };

  const startRecording = () => {
    if (callState.remoteStream) {
      const recorder = new MediaRecorder(callState.remoteStream);
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          setRecordedChunks(prev => [...prev, e.data]);
        }
      };
      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
      const blob = new Blob(recordedChunks, { type: 'video/webm' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `call-recording-${new Date().toISOString()}.webm`;
      a.click();
      URL.revokeObjectURL(url);
      setRecordedChunks([]);
      setMediaRecorder(null);
      setIsRecording(false);
    }
  };

  const performCleanup = () => {
    if (isCleaningUp.current) return;
    isCleaningUp.current = true;

    console.log('[CallInterface] Starting cleanup...');

    // Clear intervals
    if (durationInterval.current) {
      clearInterval(durationInterval.current);
      durationInterval.current = undefined;
    }
    if (statsInterval.current) {
      clearInterval(statsInterval.current);
      statsInterval.current = undefined;
    }

    // Stop recording if active
    if (isRecording && mediaRecorder) {
      try {
        if (mediaRecorder.state !== 'inactive') {
          mediaRecorder.stop();
        }
      } catch (error) {
        console.error('Error stopping media recorder:', error);
      }
    }

    // Clean up local video stream
    if (localStreamRef.current) {
      console.log('[CallInterface] Stopping local stream tracks...');
      localStreamRef.current.getTracks().forEach(track => {
        console.log(`[CallInterface] Stopping track: ${track.kind}`);
        track.stop();
      });
      localStreamRef.current = null;
    }

    // Clean up video elements
    if (localVideoRef.current) {
      const stream = localVideoRef.current.srcObject as MediaStream;
      if (stream) {
        stream.getTracks().forEach(track => {
          console.log(`[CallInterface] Stopping local video track: ${track.kind}`);
          track.stop();
        });
      }
      localVideoRef.current.srcObject = null;
    }

    if (remoteVideoRef.current) {
      const stream = remoteVideoRef.current.srcObject as MediaStream;
      if (stream) {
        stream.getTracks().forEach(track => {
          console.log(`[CallInterface] Stopping remote video track: ${track.kind}`);
          track.stop();
        });
      }
      remoteVideoRef.current.srcObject = null;
    }

    // Clean up call service streams and peer connection
    callService.cleanupCall();

    // End call history record
    if (callId.current) {
      callHistoryService.endCall(
        callId.current,
        duration,
        callQuality === 'good' ? 1 : callQuality === 'fair' ? 0.5 : 0
      );
      callId.current = undefined;
    }

    // Reset state
    setIsRecording(false);
    setMediaRecorder(null);
    setRecordedChunks([]);
    setIsLocalStreamReady(false);
    setIsOnHold(false);
    setDuration(0);
    setCallQuality('good');
    setStreamError(null);

    isCleaningUp.current = false;
  };

  return (
    <CallContainer>
      <VideoContainer>
        <RemoteVideo ref={remoteVideoRef} autoPlay playsInline />
        {isLocalStreamReady && (
          <LocalVideo ref={localVideoRef} autoPlay playsInline muted />
        )}
        {streamError && (
          <div style={{ 
            position: 'absolute', 
            top: '50%', 
            left: '50%', 
            transform: 'translate(-50%, -50%)',
            background: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            padding: '1rem',
            borderRadius: '0.5rem',
            textAlign: 'center'
          }}>
            {streamError}
          </div>
        )}
        <CallInfoBox>
          <AvatarCircle>
            {recipientName.charAt(0).toUpperCase()}
          </AvatarCircle>
          <CallInfoText>
            <CallName>{recipientName}</CallName>
            <CallDuration>{formatDuration(duration)}</CallDuration>
            <QualityIndicator quality={callQuality}>
              {callQuality === 'good' ? 'Good Connection' : 
               callQuality === 'fair' ? 'Fair Connection' : 
               'Poor Connection'}
            </QualityIndicator>
          </CallInfoText>
        </CallInfoBox>
      </VideoContainer>

      <ControlsContainer>
        <ControlButton
          onClick={handleToggleMute}
          active={!callState.isMuted}
        >
          {callState.isMuted ? <FaMicrophoneSlash /> : <FaMicrophone />}
        </ControlButton>

        <ControlButton
          onClick={handleToggleVideo}
          active={isVideoEnabled}
        >
          {isVideoEnabled ? <FaVideo /> : <FaVideoSlash />}
        </ControlButton>

        <ControlButton
          onClick={handleToggleSpeaker}
          active={callState.isSpeakerOn}
        >
          {callState.isSpeakerOn ? <FaVolumeUp /> : <FaVolumeMute />}
        </ControlButton>

        <ControlButton
          onClick={handleToggleHold}
          active={!isOnHold}
        >
          {isOnHold ? <FaPlay /> : <FaPause />}
        </ControlButton>

        <ControlButton
          className="end-call"
          onClick={handleEndCall}
        >
          <FaPhoneSlash />
        </ControlButton>
      </ControlsContainer>
    </CallContainer>
  );
};

export default CallInterface;